/**
 * Active officer abilities. Currently: air-strike — flat area damage with
 * friendly fire. Pure functions on TurnWorld, no Phaser dependency. Economy
 * (gold cost) is handled by the caller; this module handles damage + cooldown.
 */
import type { TurnWorld, Unit } from './types';
import { UNIT_DEFS } from './units';

/** Flat damage dealt to each unit in an air-strike's 3×3 area. */
export const AIR_STRIKE_DAMAGE = 4;

/** Square radius (in tiles) of an air-recon pass — a (2R+1)×(2R+1) reveal. */
export const RECON_RADIUS = 2;

/** Whether `unit` can use the ability now (it has it and the cooldown elapsed). */
export function canUseAbility(unit: Unit, abilityId: string): boolean {
  const def = UNIT_DEFS[unit.type];
  if (!def.abilities?.some((a) => a.id === abilityId)) return false;
  return (unit.cooldownState?.[abilityId] ?? 0) <= 0;
}

export interface AirStrikeResult {
  /** Ids of units that took damage (in the area). */
  hitUnitIds: number[];
  /** Ids of units destroyed by the strike. */
  killed: number[];
}

/**
 * Apply air-strike: every unit (friendly or enemy) within the 3×3 square centred
 * on (col,row) takes flat `AIR_STRIKE_DAMAGE`, bypassing defence/terrain/auras.
 * Buildings are untouched. Sets the officer's air-strike cooldown and spends its
 * turn. Does NOT deduct gold — the caller handles that.
 */
export function executeAirStrike(world: TurnWorld, officer: Unit, col: number, row: number): AirStrikeResult {
  const hitUnitIds: number[] = [];
  const killed: number[] = [];
  for (const u of world.units.values()) {
    if (Math.abs(u.col - col) <= 1 && Math.abs(u.row - row) <= 1) {
      u.hp -= AIR_STRIKE_DAMAGE;
      hitUnitIds.push(u.id);
      if (u.hp <= 0) killed.push(u.id);
    }
  }
  for (const id of killed) world.units.delete(id);

  const ability = UNIT_DEFS[officer.type].abilities?.find((a) => a.effect === 'air-strike');
  if (ability) {
    if (!officer.cooldownState) officer.cooldownState = {};
    officer.cooldownState[ability.id] = ability.cooldown;
  }
  officer.acted = true;
  officer.movePoints = 0;
  return { hitUnitIds, killed };
}

/**
 * Apply air-recon: spends the officer's turn and starts its recon cooldown. The
 * actual fog reveal is a purely visual, transient effect handled by the scene
 * (no persistent visibility change in the sim). Does NOT deduct gold.
 */
export function executeRecon(officer: Unit): void {
  const ability = UNIT_DEFS[officer.type].abilities?.find((a) => a.effect === 'recon');
  if (ability) {
    if (!officer.cooldownState) officer.cooldownState = {};
    officer.cooldownState[ability.id] = ability.cooldown;
  }
  officer.acted = true;
  officer.movePoints = 0;
}

/** Tick down all of a unit's ability cooldowns by one (called at turn start). */
export function decrementCooldowns(unit: Unit): void {
  if (!unit.cooldownState) return;
  for (const k of Object.keys(unit.cooldownState)) {
    if (unit.cooldownState[k] > 0) unit.cooldownState[k]--;
  }
}
